import { isWebPlatform } from "../../utils/platform";

// Storage keys (alphanumeric, ".", "-", and "_" only for SecureStore compatibility)
const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";
const LOGIN_AT_KEY = "auth_login_at";
const USER_KEY = "auth_user";

type TokenStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

type SecureStorage = {
  getItemAsync: (key: string) => Promise<string | null>;
  setItemAsync: (key: string, value: string) => Promise<void>;
  deleteItemAsync: (key: string) => Promise<void>;
};

// Lazy-loaded storage instances for mobile
let asyncStorage: TokenStorage | null = null;
let secureStore: SecureStorage | null = null;

/**
 * Initialize mobile storage instances
 * This should be called on mobile app startup
 */
export const initializeMobileStorage = async (
  asyncStorageInstance: TokenStorage,
  secureStoreInstance: SecureStorage,
): Promise<void> => {
  asyncStorage = asyncStorageInstance;
  secureStore = secureStoreInstance;
};

/**
 * Web storage implementation using localStorage
 */
const webStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  setAccessToken: (token: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  },
  removeAccessToken: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  },
  getRefreshToken: (): string | null => {
    // Refresh token is managed via httpOnly cookies on web
    return null;
  },
  setRefreshToken: (_token: string): void => {
    // No-op: refresh token is managed via httpOnly cookies on web
  },
  removeRefreshToken: (): void => {
    // No-op: refresh token is managed via httpOnly cookies on web
  },
  getLoginAt: (): string | null => {
    return localStorage.getItem(LOGIN_AT_KEY);
  },
  setLoginAt: (timestamp: string): void => {
    localStorage.setItem(LOGIN_AT_KEY, timestamp);
  },
  removeLoginAt: (): void => {
    localStorage.removeItem(LOGIN_AT_KEY);
  },
  getUser: (): string | null => {
    return localStorage.getItem(USER_KEY);
  },
  setUser: (user: string): void => {
    localStorage.setItem(USER_KEY, user);
  },
  removeUser: (): void => {
    localStorage.removeItem(USER_KEY);
  },
  clearAll: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(LOGIN_AT_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

/**
 * Mobile storage implementation using AsyncStorage and SecureStore
 */
const mobileStorage = {
  getAccessToken: async (): Promise<string | null> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return null;
    }
    try {
      return await asyncStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get access token from AsyncStorage:", error);
      return null;
    }
  },
  setAccessToken: async (token: string): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.setItem(ACCESS_TOKEN_KEY, token);
    } catch (error) {
      console.error("Failed to set access token in AsyncStorage:", error);
    }
  },
  removeAccessToken: async (): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to remove access token from AsyncStorage:", error);
    }
  },
  getRefreshToken: async (): Promise<string | null> => {
    if (!secureStore) {
      console.error("SecureStore not initialized");
      return null;
    }
    try {
      return await secureStore.getItemAsync(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get refresh token from SecureStore:", error);
      return null;
    }
  },
  setRefreshToken: async (token: string): Promise<void> => {
    if (!secureStore) {
      console.error("SecureStore not initialized");
      return;
    }
    try {
      await secureStore.setItemAsync(REFRESH_TOKEN_KEY, token);
    } catch (error) {
      console.error("Failed to set refresh token in SecureStore:", error);
    }
  },
  removeRefreshToken: async (): Promise<void> => {
    if (!secureStore) {
      console.error("SecureStore not initialized");
      return;
    }
    try {
      await secureStore.deleteItemAsync(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to remove refresh token from SecureStore:", error);
    }
  },
  getLoginAt: async (): Promise<string | null> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return null;
    }
    try {
      return await asyncStorage.getItem(LOGIN_AT_KEY);
    } catch (error) {
      console.error("Failed to get loginAt from AsyncStorage:", error);
      return null;
    }
  },
  setLoginAt: async (timestamp: string): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.setItem(LOGIN_AT_KEY, timestamp);
    } catch (error) {
      console.error("Failed to set loginAt in AsyncStorage:", error);
    }
  },
  removeLoginAt: async (): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.removeItem(LOGIN_AT_KEY);
    } catch (error) {
      console.error("Failed to remove loginAt from AsyncStorage:", error);
    }
  },
  getUser: async (): Promise<string | null> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return null;
    }
    try {
      return await asyncStorage.getItem(USER_KEY);
    } catch (error) {
      console.error("Failed to get user from AsyncStorage:", error);
      return null;
    }
  },
  setUser: async (user: string): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.setItem(USER_KEY, user);
    } catch (error) {
      console.error("Failed to set user in AsyncStorage:", error);
    }
  },
  removeUser: async (): Promise<void> => {
    if (!asyncStorage) {
      console.error("AsyncStorage not initialized");
      return;
    }
    try {
      await asyncStorage.removeItem(USER_KEY);
    } catch (error) {
      console.error("Failed to remove user from AsyncStorage:", error);
    }
  },
  clearAll: async (): Promise<void> => {
    await Promise.all([
      mobileStorage.removeAccessToken(),
      mobileStorage.removeRefreshToken(),
      mobileStorage.removeLoginAt(),
      mobileStorage.removeUser(),
    ]);
  },
};

/**
 * Unified token storage interface
 * Automatically uses web or mobile storage based on platform
 */
export const tokenStorage = {
  // Access Token operations
  getAccessToken: async (): Promise<string | null> => {
    if (isWebPlatform()) {
      return webStorage.getAccessToken();
    }
    return await mobileStorage.getAccessToken();
  },

  setAccessToken: async (token: string): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.setAccessToken(token);
    } else {
      await mobileStorage.setAccessToken(token);
    }
  },

  removeAccessToken: async (): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.removeAccessToken();
    } else {
      await mobileStorage.removeAccessToken();
    }
  },

  // Refresh Token operations (mobile only, web uses httpOnly cookies)
  getRefreshToken: async (): Promise<string | null> => {
    if (isWebPlatform()) {
      return webStorage.getRefreshToken();
    }
    return await mobileStorage.getRefreshToken();
  },

  setRefreshToken: async (token: string): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.setRefreshToken(token);
    } else {
      await mobileStorage.setRefreshToken(token);
    }
  },

  removeRefreshToken: async (): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.removeRefreshToken();
    } else {
      await mobileStorage.removeRefreshToken();
    }
  },

  // LoginAt operations
  getLoginAt: async (): Promise<string | null> => {
    if (isWebPlatform()) {
      return webStorage.getLoginAt();
    }
    return await mobileStorage.getLoginAt();
  },

  setLoginAt: async (timestamp: string): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.setLoginAt(timestamp);
    } else {
      await mobileStorage.setLoginAt(timestamp);
    }
  },

  removeLoginAt: async (): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.removeLoginAt();
    } else {
      await mobileStorage.removeLoginAt();
    }
  },

  // User operations
  getUser: async (): Promise<string | null> => {
    if (isWebPlatform()) {
      return webStorage.getUser();
    }
    return await mobileStorage.getUser();
  },

  setUser: async (user: string): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.setUser(user);
    } else {
      await mobileStorage.setUser(user);
    }
  },

  removeUser: async (): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.removeUser();
    } else {
      await mobileStorage.removeUser();
    }
  },

  // Clear all tokens
  clearAll: async (): Promise<void> => {
    if (isWebPlatform()) {
      webStorage.clearAll();
    } else {
      await mobileStorage.clearAll();
    }
  },
};
